export interface TrelloRestApiClient {
  authorize(options: TrelloAuthorizationOptions): Promise<string>;
  clearToken(): Promise<void>;
  getToken(): Promise<string | null>;
  isAuthorized(): Promise<boolean>;
}

export interface TrelloAuthorizationOptions {
  expiration: "never";
  scope: "read" | "read,write";
}

export interface TrelloModalOptions {
  accentColor: string;
  fullscreen: boolean;
  height: number;
  title: string;
  url: string;
}

export interface TrelloPowerUpContext {
  getRestApi(): Promise<TrelloRestApiClient>;
  modal(options: TrelloModalOptions): unknown;
}

export interface TrelloPowerUpIframeContext {
  board(...fields: string[]): Promise<{ id: string }>;
  getRestApi(): Promise<TrelloRestApiClient>;
  jwt(options: { state: string }): Promise<string>;
  render(callback: () => void): void;
  sizeTo(target: Element | number | string): Promise<void>;
}

export interface TrelloThemeIcons {
  dark: string;
  light: string;
}

export interface TrelloBoardButton {
  callback(context: TrelloPowerUpContext): unknown;
  condition: "admin";
  icon: TrelloThemeIcons;
  text: string;
}

export interface TrelloPowerUpCapabilities {
  "authorization-status"(
    context: TrelloPowerUpContext,
  ): Promise<{ authorized: boolean }>;
  "board-buttons"(): readonly TrelloBoardButton[];
  "show-authorization"(context: TrelloPowerUpContext): unknown;
  "show-settings"(context: TrelloPowerUpContext): unknown;
}

export interface TrelloPowerUpInitializeOptions {
  appAuthor: string;
  appKey: string;
  appName: string;
}

export interface TrelloPowerUpGlobal {
  iframe(options: TrelloPowerUpInitializeOptions): TrelloPowerUpIframeContext;
  initialize(
    capabilities: TrelloPowerUpCapabilities,
    options: TrelloPowerUpInitializeOptions,
  ): void;
}

export interface BoardPreserveConnectorConfig extends TrelloPowerUpInitializeOptions {
  authorizationUrl: string;
  boardButtonIcons: TrelloThemeIcons;
  modalUrl: string;
  settingsUrl: string;
}

export function readConnectorConfig(
  apiKey: string | undefined,
  origin: string,
): BoardPreserveConnectorConfig | null {
  const normalizedApiKey = apiKey?.trim();

  if (!normalizedApiKey) {
    return null;
  }

  return {
    appAuthor: "Nodestone Labs LTD",
    appKey: normalizedApiKey,
    appName: "Board Preserve",
    authorizationUrl: new URL("/?entry=authorization", origin).toString(),
    boardButtonIcons: {
      dark: new URL("/icons/board-preserve-dark.svg", origin).toString(),
      light: new URL("/icons/board-preserve-light.svg", origin).toString(),
    },
    modalUrl: new URL("/", origin).toString(),
    settingsUrl: new URL("/?entry=settings", origin).toString(),
  };
}

export function createPowerUpCapabilities(
  config: BoardPreserveConnectorConfig,
): TrelloPowerUpCapabilities {
  return {
    "authorization-status": async (context) => ({
      authorized: await (await context.getRestApi()).isAuthorized(),
    }),
    "board-buttons": () => [
      {
        callback: (context) =>
          context.modal({
            accentColor: "#10243e",
            fullscreen: false,
            height: 680,
            title: "Board Preserve",
            url: config.modalUrl,
          }),
        condition: "admin",
        icon: config.boardButtonIcons,
        text: "Board Preserve",
      },
    ],
    "show-authorization": (context) =>
      context.modal({
        accentColor: "#10243e",
        fullscreen: false,
        height: 520,
        title: "Authorize Board Preserve",
        url: config.authorizationUrl,
      }),
    "show-settings": (context) =>
      context.modal({
        accentColor: "#10243e",
        fullscreen: false,
        height: 620,
        title: "Board Preserve settings",
        url: config.settingsUrl,
      }),
  };
}
